import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { Player, RoundBye, RoundByeType } from '@swiss-magic/shared-types';

// Story 095 — byes on demand. Opened by the generate-pairings button. The
// director optionally marks players for a per-round bye (half / zero / full),
// then confirms to pair the rest. Confirming with no byes is the normal
// "just pair" path.

type Selection = 'none' | RoundByeType;

interface Props {
  open: boolean;
  roundNumber: number;
  players: Player[];
  /**
   * Story 105 — ids of players who have secured a TOP X place (KO qualifier
   * mode). Full-point byes are pre-selected for them; the director can clear a
   * bye to keep a secured player in the draw (e.g. for playoff seeding).
   */
  securedPlayerIds?: string[];
  /** True while the secured-spot check is still running (worker search,
   *  hard-bounded at ~20s). Drives the progress indicator. */
  securedPending?: boolean;
  generating: boolean;
  onCancel: () => void;
  onConfirm: (byes: RoundBye[]) => void;
}

export function GeneratePairingsByesDialog({
  open,
  roundNumber,
  players,
  securedPlayerIds = [],
  securedPending = false,
  generating,
  onCancel,
  onConfirm,
}: Props) {
  const { t } = useTranslation('director');
  const [selections, setSelections] = useState<Record<string, Selection>>({});

  // Start from a clean slate whenever the dialog opens.
  useEffect(() => {
    if (open) setSelections({});
  }, [open]);

  // Pre-select a full-point bye for each secured player once the secured set
  // arrives from the backend — WITHOUT clobbering anything the director has
  // already chosen. The fetch races the director's own clicks (2026-09-19:
  // a slow response used to re-initialize the state and silently wipe
  // manually assigned byes just before confirm).
  useEffect(() => {
    if (!open || securedPlayerIds.length === 0) return;
    setSelections((prev) => {
      const merged = { ...prev };
      for (const id of securedPlayerIds) {
        if (!(id in merged)) merged[id] = 'full';
      }
      return merged;
    });
  }, [open, securedPlayerIds]);

  function setBye(playerId: string, value: Selection) {
    setSelections((prev) => ({ ...prev, [playerId]: value }));
  }

  const securedSet = new Set(securedPlayerIds);
  const securedNames = players
    .filter((p) => securedSet.has(p.id))
    .map((p) => `${p.firstName} ${p.lastName}`);

  function confirm() {
    const byes: RoundBye[] = players
      .map((p) => ({ playerId: p.id, type: selections[p.id] ?? 'none' }))
      .filter((b): b is RoundBye => b.type !== 'none');
    onConfirm(byes);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setSelections({});
          onCancel();
        }
      }}
    >
      <DialogContent data-testid="generate-pairings-dialog">
        <DialogHeader>
          <DialogTitle>
            {t('rounds_page.byes_dialog.title', { round: roundNumber })}
          </DialogTitle>
          <DialogDescription>
            {t('rounds_page.byes_dialog.description')}
          </DialogDescription>
        </DialogHeader>

        {securedPending && (
          <SecuredPendingIndicator open={open} />
        )}

        {securedNames.length > 0 && (
          <div
            data-testid="secured-players-notice"
            className="rounded-md border border-primary/30 bg-primary/5 p-3 text-sm"
          >
            <p className="font-medium">
              {t('rounds_page.byes_dialog.secured_title', {
                n: securedNames.length,
              })}
            </p>
            <p className="mt-1 text-muted-foreground">
              {securedNames.join(', ')}
            </p>
            <p className="mt-1 text-muted-foreground">
              {t('rounds_page.byes_dialog.secured_hint')}
            </p>
          </div>
        )}

        <div className="max-h-80 overflow-y-auto">
          {players.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('rounds_page.byes_dialog.no_players')}
            </p>
          ) : (
            players.map((p) => {
              const fullName = `${p.firstName} ${p.lastName}`;
              return (
                <div
                  key={p.id}
                  className="flex items-center justify-between gap-3 border-b py-1.5 text-sm last:border-b-0"
                >
                  <span>{fullName}</span>
                  <select
                    data-testid={`bye-select-${fullName}`}
                    value={selections[p.id] ?? 'none'}
                    onChange={(e) => setBye(p.id, e.target.value as Selection)}
                    className="h-9 rounded-md border border-input bg-transparent px-2 text-sm dark:bg-input/30"
                  >
                    <option value="none">
                      {t('rounds_page.byes_dialog.bye_none')}
                    </option>
                    <option value="half">
                      {t('rounds_page.byes_dialog.bye_half')}
                    </option>
                    <option value="zero">
                      {t('rounds_page.byes_dialog.bye_zero')}
                    </option>
                    <option value="full">
                      {t('rounds_page.byes_dialog.bye_full')}
                    </option>
                  </select>
                </div>
              );
            })
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={generating}>
            {t('rounds_page.byes_dialog.cancel')}
          </Button>
          <Button
            data-testid="confirm-generate-pairings"
            onClick={confirm}
            disabled={generating}
          >
            {generating
              ? t('rounds_page.buttons.generating')
              : t('rounds_page.byes_dialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Honest progress for the secured-spot check: a true percent of the search
// is unknowable (branch-and-bound — tree size is only known once finished),
// so the bar tracks elapsed time against the search's HARD budget (20s,
// matching KoEliminationService's worker timeBudgetMs). It completes early
// the instant the response lands; the label keeps the director informed
// enough to decide whether to wait or generate without proposals.
const SECURED_BUDGET_MS = 20_000;

function SecuredPendingIndicator({ open }: { open: boolean }) {
  const { t } = useTranslation('director');
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!open) return;
    setElapsed(0);
    const started = Date.now();
    const timer = setInterval(() => setElapsed(Date.now() - started), 250);
    return () => clearInterval(timer);
  }, [open]);
  const pct = Math.min(99, Math.round((elapsed / SECURED_BUDGET_MS) * 100));
  const secondsLeft = Math.max(0, Math.ceil((SECURED_BUDGET_MS - elapsed) / 1000));
  return (
    <div
      data-testid="secured-pending"
      className="rounded-md border border-muted bg-muted/30 p-3 text-sm"
    >
      <p className="font-medium">
        {t('rounds_page.byes_dialog.secured_pending_title')}
      </p>
      <div className="mt-2 h-2 w-full overflow-hidden rounded bg-muted">
        <div
          className="h-full bg-primary transition-all duration-300"
          style={{ width: pct + '%' }}
        />
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {t('rounds_page.byes_dialog.secured_pending_hint', {
          seconds: secondsLeft,
        })}
      </p>
    </div>
  );
}
